import { useEffect, useState } from 'react';
import { flashcardApi } from '../api/client';
import { Brain, RotateCcw, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';

interface DueCard {
  reviewId:    string;
  flashcardId: string;
  frontText:   string;
  backText:    string;
  cardType:    string;
  reviewCount: number;
  intervalDays: number;
}

type Difficulty = 'EASY' | 'MEDIUM' | 'HARD' | 'FORGOT';

export function FlashcardsPage() {
  const [cards, setCards]       = useState<DueCard[]>([]);
  const [current, setCurrent]   = useState(0);
  const [flipped, setFlipped]   = useState(false);
  const [loading, setLoading]   = useState(true);
  const [done, setDone]         = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadDueCards();
  }, []);

  const loadDueCards = async () => {
    setLoading(true);
    try {
      const res = await flashcardApi.getDueToday();
      setCards(res.data);
      if (res.data.length === 0) setDone(true);
    } catch {
      toast.error('Failed to load flashcards');
    } finally {
      setLoading(false);
    }
  };

  const handleDifficulty = async (difficulty: Difficulty) => {
    if (submitting) return;
    setSubmitting(true);

    try {
      const card = cards[current];
      await flashcardApi.submitReview(card.reviewId, difficulty);

      if (current + 1 >= cards.length) {
        setDone(true);
        toast.success('All cards reviewed for today! 🎉');
      } else {
        setCurrent(prev => prev + 1);
        setFlipped(false);
      }
    } catch {
      toast.error('Failed to save review');
    } finally {
      setSubmitting(false);
    }
  };

  const difficultyButtons = [
    { label: 'Forgot',  value: 'FORGOT' as Difficulty,
      color: 'bg-red-500/20 text-red-400 border-red-500/40',
      emoji: '😕' },
    { label: 'Hard',    value: 'HARD' as Difficulty,
      color: 'bg-orange-500/20 text-orange-400 border-orange-500/40',
      emoji: '😤' },
    { label: 'Medium',  value: 'MEDIUM' as Difficulty,
      color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
      emoji: '🤔' },
    { label: 'Easy',    value: 'EASY' as Difficulty,
      color: 'bg-green-500/20 text-green-400 border-green-500/40',
      emoji: '😊' },
  ];

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-8 text-gray-500">
        Loading your flashcards...
      </div>
    );
  }

  if (done || cards.length === 0) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-8 text-center">
        <div className="text-6xl mb-4">🎉</div>
        <h1 className="text-2xl font-black text-white mb-2">
          All done for today!
        </h1>
        <p className="text-gray-400 text-sm mb-6">
          {cards.length === 0
            ? "No flashcards due for review. Complete lessons to generate cards."
            : "You have reviewed all your cards. Come back tomorrow!"}
        </p>
        <div className="bg-gray-900 border border-gray-800
                        rounded-2xl p-6 text-left">
          <div className="text-xs font-bold text-green-400 mb-2">
            HOW SPACED REPETITION WORKS
          </div>
          <p className="text-gray-400 text-sm leading-relaxed">
            Cards you found <span className="text-green-400">Easy</span> will
            return in 3x longer intervals. Cards you
            <span className="text-red-400"> Forgot</span> return tomorrow.
            This is how you remember things for life, not just the exam.
          </p>
        </div>
      </div>
    );
  }

  const card = cards[current];

  return (
    <div className="max-w-2xl mx-auto px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-2">
          <Brain size={20} className="text-purple-400" />
          <h1 className="text-lg font-black text-white">
            Flashcard Review
          </h1>
        </div>
        <div className="text-sm text-gray-500">
          {current + 1} / {cards.length}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-800 rounded-full mb-6">
        <div
          className="h-1 bg-purple-400 rounded-full transition-all"
          style={{ width: `${((current + 1) / cards.length) * 100}%` }}
        />
      </div>

      {/* Card */}
      <div
        onClick={() => setFlipped(!flipped)}
        className={`min-h-64 rounded-2xl border cursor-pointer
                    flex flex-col items-center justify-center p-8
                    transition-all duration-300 mb-6 text-center
          ${flipped
            ? 'bg-purple-400/10 border-purple-400/30'
            : 'bg-gray-900 border-gray-800 hover:border-gray-700'
          }`}
      >
        <div className="text-xs font-bold mb-4 uppercase tracking-wider
                        text-gray-500">
          {flipped ? 'Answer' : 'Question — tap to reveal'}
        </div>

        <p className={`text-lg font-semibold leading-relaxed
          ${flipped ? 'text-purple-200' : 'text-white'}`}>
          {flipped ? card.backText : card.frontText}
        </p>

        {!flipped && (
          <div className="mt-4 text-gray-600 text-xs flex items-center gap-1">
            <RotateCcw size={10} />
            Tap to flip
          </div>
        )}
      </div>

      {/* Difficulty buttons — only show after flipping */}
      {flipped ? (
        <div>
          <div className="text-xs text-center text-gray-500 mb-3">
            How well did you know this?
          </div>
          <div className="grid grid-cols-4 gap-2">
            {difficultyButtons.map(btn => (
              <button
                key={btn.value}
                onClick={() => handleDifficulty(btn.value)}
                disabled={submitting}
                className={`border rounded-xl py-3 text-sm font-bold
                            transition-all hover:opacity-80
                            disabled:opacity-50 flex flex-col
                            items-center gap-1 ${btn.color}`}
              >
                <span className="text-lg">{btn.emoji}</span>
                {btn.label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="text-center">
          <button
            onClick={() => setFlipped(true)}
            className="flex items-center gap-2 mx-auto text-purple-400
                       hover:text-purple-300 transition-colors text-sm"
          >
            Reveal answer
            <ChevronRight size={14} />
          </button>
        </div>
      )}

      {/* Card info */}
      <div className="mt-4 text-center text-xs text-gray-600">
        Reviewed {card.reviewCount} times ·
        Current interval: {card.intervalDays} days
      </div>
    </div>
  );
}